<script setup lang="ts">
  import GlobalAiChat from '@/components/app/chat/global/GlobalAiChat.vue'
  import GlobalAiChatIntro from '@/components/app/chat/global/GlobalAiChatIntro.vue'
  import { useMetrics } from '@/composables/useMetrics'
  import { useUserStore } from '@/shared/stores/userStore'
  import { useGlobalChatStore } from '@/store/app/globalChat/globalChatStore'
  import { AIChatSessionTypeEnum, EventNameEnum } from '@/types/generated'
  import { Loader2 } from 'lucide-vue-next'
  import { computed, onMounted, provide, ref } from 'vue'
  import { useRoute } from 'vue-router'

  const route = useRoute()
  const globalChatStore = useGlobalChatStore()
  const userStore = useUserStore()
  const { trackEvent } = useMetrics()

  // State
  const activeFolderId = ref<string | null>(null)
  const showIntro = ref(false)
  const isLoading = ref(true)

  // Computed
  const activeSessionGroup = computed(() => {
    if (!activeFolderId.value) return null
    return (
      globalChatStore.sessionGroups.find((group) => group.uuid === activeFolderId.value) || null
    )
  })

  // Provide global context to child components
  provide('sessionType', AIChatSessionTypeEnum.GLOBAL)
  provide('sessionGroupData', activeSessionGroup)

  const handleIntroAccepted = () => {
    showIntro.value = false
  }

  // Initialize
  onMounted(async () => {
    try {
      // Fetch user to check if intro has been seen
      if (!userStore.user) {
        await userStore.fetchUser()
      }

      // Check if user has seen the intro
      showIntro.value = !(userStore.user as any)?.global_ai_chat_intro_seen

      // Track navigation click - dismiss badge if not already dismissed
      if (userStore.user && !(userStore.user as any)?.global_ai_chat_badge_dismissed) {
        try {
          const { UsersService } = await import('@/types/generated')
          await UsersService.usersPartialUpdate({
            uuid: userStore.user.uuid!,
            requestBody: {
              global_ai_chat_badge_dismissed: true
            } as any
          })
          // Update local user store
          if (userStore.user) {
            ;(userStore.user as any).global_ai_chat_badge_dismissed = true
          }
        } catch (error) {
          console.error('Failed to dismiss badge:', error)
        }
      }

      // Track Global AI Chat visited event
      await trackEvent(EventNameEnum.GLOBAL_AI_CHAT_VISITED)

      // Load session groups
      await globalChatStore.loadSessionGroups()

      // Load global prompt templates
      await globalChatStore.loadGlobalPromptTemplates()

      // Load standalone global chat sessions
      await globalChatStore.loadStandaloneGlobalChatSessions()

      // Set active folder from route params
      const groupUuid = route.params.groupUuid as string
      if (groupUuid) {
        activeFolderId.value = groupUuid
        // Load chat sessions for this folder
        await globalChatStore.loadChatSessions(groupUuid)
      }
    } finally {
      isLoading.value = false
    }
  })
</script>

<template>
  <div class="flex h-[calc(100vh-60px)] w-full">
    <!-- Loading state while checking user status -->
    <div
      v-if="isLoading"
      class="flex h-full w-full items-center justify-center bg-gray-50"
    >
      <Loader2 class="h-8 w-8 animate-spin text-gray-400" />
    </div>
    <!-- Show intro if user hasn't seen it -->
    <GlobalAiChatIntro
      v-else-if="showIntro"
      @intro-accepted="handleIntroAccepted"
    />
    <!-- Global AI Chat with integrated sidebar -->
    <GlobalAiChat
      v-else
      :session-group-uuid="activeFolderId || undefined"
      :session-group="activeSessionGroup || undefined"
    />
  </div>
</template>
